import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable } from 'rxjs';
import * as _ from 'lodash-es';

@Injectable({
  providedIn: 'root'
})
export class WithholdTaxSummaryEditService {

  constructor(private http: HttpClient) { }

  /**
   * Save Non-Creditable Withholding Tax data
   * Matching AngularJS: WithHoldTaxFactory.savePivotedData (action_code=xyw55b)
   */
  saveNonCreditableData(
    baseUrl: string,
    returnClobSettingsObj: any,
    defaultsChangeDetails: any[],
    globalService: any,
    filterParams: any = {},
    rowData: any = {}
  ): Observable<any> {
    const baseUrlClean = baseUrl.endsWith('/') ? baseUrl.slice(0, -1) : baseUrl;
    const url = `${baseUrlClean}/saveJsonAsPivotObject?action_code=xyw55b`;

    const jsonObj = JSON.stringify(defaultsChangeDetails);
    const jsonSettings = JSON.stringify(returnClobSettingsObj);

    let params = _.merge({}, globalService?.globalParams || {}, filterParams);
    params = _.extend({ clob_data: jsonObj }, params);
    params = _.extend({ clob_settings: jsonSettings }, params);

    // Build checkLockedbj from rowData
    const checkLockedbj = [{
      "leid": rowData.LEID,
      "me_code": rowData.ME_CODE,
      "cdr_number": rowData.CDR_NO || rowData.MARS,
      "reporting_period": rowData.REPORTING_PERIOD,
      "combination_key": returnClobSettingsObj.object_id || rowData.COMBINATION_KEY,
      "scenario_key": parseInt(returnClobSettingsObj.scenario || filterParams.scenario || rowData.SCENARIO || "1")
    }];
    params.checkLockedbj = JSON.stringify(checkLockedbj);

    // Ensure required parameters
    if (!params.tax_year) {
      params.tax_year = filterParams.tax_year || rowData.TAX_YEAR || returnClobSettingsObj?.tax_year || globalService?.globalParams?.tax_year || '';
    }
    if (!params.scenario) {
      params.scenario = filterParams.scenario || rowData.SCENARIO || returnClobSettingsObj?.scenario || globalService?.globalParams?.scenario || '';
    }
    if (!params.jcd_key) {
      params.jcd_key = filterParams.jcd_key || rowData.JCD_KEY || returnClobSettingsObj?.jcd_key || globalService?.globalParams?.jcd_key || 250;
    }
    
    // Add scenarioCode and is_issue_key_enabled (matching AngularJS JsonObjectFactory behavior)
    if (!params.scenarioCode && filterParams.scenarioCode) {
      params.scenarioCode = filterParams.scenarioCode;
    }
    if (!params.is_issue_key_enabled && filterParams.is_issue_key_enabled) {
      params.is_issue_key_enabled = filterParams.is_issue_key_enabled;
    }

    const urlEncoded = new URLSearchParams();
    Object.keys(params).forEach(key => {
      const value = params[key];
      if (value !== undefined && value !== null) {
        urlEncoded.set(key, value.toString());
      }
    });

    const headers = new HttpHeaders({
      'Content-Type': 'application/x-www-form-urlencoded'
    });

    return this.http.post<any>(url, urlEncoded.toString(), { headers, withCredentials: true });
  }

  /**
   * Save Creditable Withholding Tax data (WHT on Dividend)
   * Matching AngularJS: WithHoldTaxFactory.saveWHCreditablePivotedData (action_code=2cn72k)
   */
  saveCreditableData(
    baseUrl: string,
    returnObj: any,
    defaultsChangeDetails: any[],
    globalService: any,
    filterParams: any = {},
    rowData: any = {}
  ): Observable<any> {
    const baseUrlClean = baseUrl.endsWith('/') ? baseUrl.slice(0, -1) : baseUrl;
    const url = `${baseUrlClean}/saveJsonAsPivotObject?action_code=2cn72k`;

    const jsonObj = JSON.stringify(defaultsChangeDetails);
    const jsonSettings = JSON.stringify(returnObj);

    let params = _.merge({}, globalService?.globalParams || {}, filterParams);
    params = _.extend({ clob_data: jsonObj }, params);
    params = _.extend({ clob_settings: jsonSettings }, params);
    
    // Merge returnObj into params (matching AngularJS: actual_params = _.assign({}, _data, params))
    params = _.assign({}, returnObj, params);

    // Handle null wht_dividend explicitly (matching AngularJS line 57-59)
    if (returnObj['wht_dividend'] === null) {
      params['wht_dividend'] = null;
    }

    // Build checkLockedbj
    const checkLockedbj = [{
      "leid": rowData.LEID,
      "me_code": rowData.ME_CODE,
      "cdr_number": rowData.CDR_NO || rowData.MARS,
      "reporting_period": rowData.REPORTING_PERIOD,
      "combination_key": returnObj.combination_key || rowData.COMBINATION_KEY,
      "scenario_key": parseInt(returnObj.scenario || filterParams.scenario || rowData.SCENARIO || "1")
    }];
    params.checkLockedbj = JSON.stringify(checkLockedbj);

    // Ensure required parameters
    if (!params.tax_year) {
      params.tax_year = filterParams.tax_year || rowData.TAX_YEAR || returnObj?.tax_year || globalService?.globalParams?.tax_year || '';
    }
    if (!params.scenario) {
      params.scenario = filterParams.scenario || rowData.SCENARIO || returnObj?.scenario || globalService?.globalParams?.scenario || '';
    }
    if (!params.jcd_key) {
      params.jcd_key = filterParams.jcd_key || rowData.JCD_KEY || returnObj?.jcd_key || globalService?.globalParams?.jcd_key || 250;
    }
    
    // Add scenarioCode and is_issue_key_enabled (matching AngularJS JsonObjectFactory behavior)
    if (!params.scenarioCode && filterParams.scenarioCode) {
      params.scenarioCode = filterParams.scenarioCode;
    }
    if (!params.is_issue_key_enabled && filterParams.is_issue_key_enabled) {
      params.is_issue_key_enabled = filterParams.is_issue_key_enabled;
    }

    const urlEncoded = new URLSearchParams();
    Object.keys(params).forEach(key => {
      const value = params[key];
      if (value !== undefined && value !== null) {
        urlEncoded.set(key, value.toString());
      }
    });

    const headers = new HttpHeaders({
      'Content-Type': 'application/x-www-form-urlencoded'
    });

    return this.http.post<any>(url, urlEncoded.toString(), { headers, withCredentials: true });
  }

  /**
   * Get Tag Details Data
   * Matching AngularJS: action_code='sopgak'
   */
  getTagDetailsData(
    baseUrl: string,
    combinationKey: any,
    taxYear: any,
    scenario: any,
    globalService: any
  ): Observable<any> {
    const baseUrlClean = baseUrl.endsWith('/') ? baseUrl.slice(0, -1) : baseUrl;
    const url = `${baseUrlClean}/loadJsonObject`;

    const params: any = {
      action_code: 'sopgak',
      combination_key: combinationKey,
      tax_year: taxYear,
      scenario: scenario
    };

    const urlEncoded = new URLSearchParams();
    Object.keys(params).forEach(key => {
      if (params[key] !== undefined && params[key] !== null) {
        urlEncoded.set(key, params[key].toString());
      }
    });

    const headers = new HttpHeaders({
      'Content-Type': 'application/x-www-form-urlencoded'
    });

    return this.http.post<any>(url, urlEncoded.toString(), { headers, withCredentials: true });
  }

  /**
   * Get Summary Information
   * Matching AngularJS: action_code='35n2lz'
   */
  getSummaryInformation(
    baseUrl: string,
    params: any,
    globalService: any
  ): Observable<any> {
    const baseUrlClean = baseUrl.endsWith('/') ? baseUrl.slice(0, -1) : baseUrl;
    const url = `${baseUrlClean}/loadJsonObject?action_code=35n2lz`;

    const urlEncoded = new URLSearchParams();
    if (params) {
      Object.keys(params).forEach(key => {
        if (params[key] !== undefined && params[key] !== null) {
          urlEncoded.set(key, params[key].toString());
        }
      });
    }

    const headers = new HttpHeaders({
      'Content-Type': 'application/x-www-form-urlencoded'
    });

    return this.http.post<any>(url, urlEncoded.toString(), { headers, withCredentials: true });
  }
}
